import DOMPurify from 'dompurify';

export const restrictNonIntPoints = event => {
  if (event.key === '.' || event.key === 'e' || event.key === '-') {
    event.preventDefault();
  }
};

// A named (non-`_blank`) target leaves `window.opener` reachable from the opened page, so `rel` is
// forced back onto every targeted anchor rather than trusted from the authored markup.
const forceOpenerSafeRel = (node: Element) => {
  if (node.nodeName === 'A' && node.hasAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
};

// DOMPurify strips `target` by default, which would silently break the editor-authored CTA buttons
// in every preview that renders saved HTML. PLA-9141
export const sanitizeMarkup = (text: string) => {
  DOMPurify.addHook('afterSanitizeAttributes', forceOpenerSafeRel);
  try {
    return { __html: DOMPurify.sanitize(text, { ADD_ATTR: ['target'] }) };
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
};

// Sanitize untrusted SVG markup (e.g. merchant-authored custom bubble svg_code).
// Uses DOMPurify's SVG profile, which strips <script>, <foreignObject>, event
// handlers and unsafe/namespaced protocols that a regex sanitizer misses.
export const sanitizeSvg = (svgCode: string): string => {
  return DOMPurify.sanitize(svgCode || '', {
    USE_PROFILES: { svg: true, svgFilters: true }
  });
};

export const removeDuplicates = (myArr, prop) => {
  return myArr.filter((obj, pos, arr) => {
    return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
  });
};

export const memoizeInputComponent = self => {
  return (key, f) => {
    self.__memoizeddInputFuncGenerator = self.__memoizeddInputFuncGenerator
      ? self.__memoizeddInputFuncGenerator
      : {};
    self.__memoizeddInputFuncGenerator[key] = self.__memoizeddInputFuncGenerator[key]
      ? self.__memoizeddInputFuncGenerator[key]
      : f;
    return self.__memoizeddInputFuncGenerator[key];
  };
};

export const unmemoizeInputComponent = self => {
  return key => {
    if (key === undefined) {
      self.__memoizeddInputFuncGenerator = {};
    } else {
      if (self.__memoizeddInputFuncGenerator) {
        delete self.__memoizeddInputFuncGenerator[key];
      }
    }
  };
};

export const formatNumber = function (number, n?, x?) {
  return !number || isNaN(number) ? 0 : number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const scrollTo = (top = 0, left = 0) => {
  try {
    window.scrollTo({ top, left, behavior: 'smooth' });
  } catch (err) {
    if (err instanceof TypeError) {
      window.scroll(top, left);
    } else {
      throw err;
    }
  }
};

export const removeEmptyFields = (jsonValues, excludeKeys: string[] = []) =>
  Object.entries(jsonValues).reduce((a, [k, v]) => {
    if (excludeKeys.some(prefix => k.startsWith(prefix))) {
      return { ...a, [k]: v };
    }
    return v == null || v === '' ? a : { ...a, [k]: v };
  }, {});

/**
 *
 * @param obj
 * Replaces the empty-string values of the arg object with null, recursively.
 */
export const replaceEmptyStringsWithNull = (obj: Object) => {
  const shallowCopy = Array.isArray(obj) ? [...obj] : { ...obj };

  for (const [key, value] of Object.entries(shallowCopy)) {
    if (typeof value === 'string' && value === '') {
      shallowCopy[key] = null;
    } else if (value?.constructor === Object || value?.constructor === Array) {
      shallowCopy[key] = replaceEmptyStringsWithNull(value);
    }
  }

  return shallowCopy;
};

/**
 * @param {number} v
 * @param {string} singular
 */
export const pluralize = (v, singular) => {
  return `${v} ${singular}${v === 1 ? '' : 's'}`;
};

export const numberWithCommas = x => {
  if (x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  } else {
    return '0';
  }
};

export const getDeepKeys = obj => {
  let keys = [];
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        let subKeys = getDeepKeys(obj[key]);
        keys = keys.concat(
          subKeys.map(subkey => {
            return key + '.' + subkey;
          })
        );
      } else if (Array.isArray(obj[key])) {
        for (let i = 0; i < obj[key].length; i++) {
          let subKeys = getDeepKeys(obj[key][i]);
          keys = keys.concat(
            subKeys.map(subkey => {
              return `${key}[${i}].${subkey}`;
            })
          );
        }
      } else {
        keys.push(key);
      }
    }
  }
  return keys;
};

export const isRichTextEmpty = richText => {
  return (
    richText === '<p><br></p>' || richText === '<p><br></p> ' || richText === 'N.A' || !richText
  );
};

export const findCategoryWithParentLabel = (categories = [], id, parentLabelKey) => {
  const safeCategories = Array.isArray(categories) ? categories : [];
  let parentLabel = null;
  let found = null;

  for (let index = 0; index < safeCategories.length; index++) {
    const category = safeCategories[index];
    if (category.id === id) {
      found = category;
      break;
    }

    if (!found && category.children) {
      for (let subIndex = 0; subIndex < category.children.length; subIndex++) {
        const child = category.children[subIndex];
        if (child.id === id) {
          parentLabel = category[parentLabelKey];
          found = child;
          break;
        }
      }
    }
  }

  return { found, parentLabel };
};

export const calulatePercentage = (number: number, total: number, decimalPoint: number) => {
  if (total < 1) {
    return '0';
  }
  return ((number / total) * 100).toFixed(decimalPoint) + '%';
};

export const cdnURL = <S extends string>(path: S) => {
  const baseName = `https://cdn.perxtech.net/content/dashboard`;
  return `${baseName}/${path}` as `${typeof baseName}/${S}`;
};

export const satisfyPermission = (record, actions, restricted_fields) => {
  return (
    actions.includes('edit') &&
    (!restricted_fields.includes('state') ||
      (restricted_fields.includes('state') && record.state === 'draft'))
  );
};
